import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { useAsanaData } from 'Shared/utils/hooks';
import { createAsanaTask } from 'Shared/actions/todos';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';

import AsanaDots from 'Shared/images/AsanaDots.png';

import './CreateAsanaTaskModal.scss';

type Props = {
  itemUuid: string;
  isOpen: boolean;
  onToggle: () => void;
};

const CreateAsanaTaskModal = ({ itemUuid, isOpen, onToggle }: Props) => {
  const dispatch = useDispatch();
  const [project, setProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const asanaData = useAsanaData();
  const projects = asanaData?.projects;
  const optionsForSelect = projects ? projects.map(({ id, name }) => ({ value: id, label: name })) : [];

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Copy to Asana' });
  }, []);

  const handleSelectChange = value => {
    setProject(value);
    return null;
  };

  const handleCreateClick = () => {
    setSaving(true);
    dispatch(createAsanaTask({ itemUuid, projectId: project.value }))
      .then(() => WpToastManager.success('Success'))
      .catch(() => WpToastManager.danger('Something went wrong! Please try again'))
      .finally(() => {
        onToggle();
        setSaving(false);
      });
  };

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>
        Copy To Asana
        <img className="CreateAsanaTaskModal__logo ml-3" src={AsanaDots} alt="" />
      </WpModalHeader>
      <WpModalBody>
        <div className="CreateAsanaTaskModal">
          <div>Choose the Asana project you would like to copy this item to.</div>
          <div className="small mb-2 mt-1 text-muted">
            * Note: Any subtopics linked to this item will not be exported to Asana
          </div>

          <div className="CreateAsanaTaskModal__label">Projects</div>
          <Select
            isSearchable
            options={optionsForSelect}
            placeholder="Choose project"
            value={project}
            onChange={handleSelectChange}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: '1050' }) }}
          />
          <div className="CreateAsanaTaskModal__spacer mt-1">
            {project && (
              <div className="CreateAsanaTaskModal__note">Item will be copied to the top of the selected project.</div>
            )}
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" disabled={saving} onClick={onToggle}>
            Cancel
          </WpButton>
          <WpButton disabled={saving || !project} onClick={handleCreateClick} loading={saving}>
            Create Task
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CreateAsanaTaskModal;
